/**
 * Windows a player can move, which stay where they were put.
 *
 * A bank and a shop are looked at rather than glanced at, so where they sit is a
 * preference rather than a layout decision. Each kind remembers its own place, so
 * the bank is always where the player left the bank and the shop where they left
 * the shop.
 *
 * Where that is, for the size of screen the game is on, is the layout's to say:
 * see `layout.ts`. This is the dragging.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { keepPosition, LAYOUT_EVENT, positionFor } from './layout';

export interface WindowPosition {
  x: number;
  y: number;
}

/**
 * How close an edge has to be before it takes hold, in pixels.
 *
 * Small enough that a window can still be put anywhere, large enough that
 * lining two of them up is a shove rather than a surgery.
 */
const SNAP = 10;

/** Travel past which a press counts as a drag rather than a click, in pixels. */
const DRAG_SLOP = 4;

/** Kept fully on screen, with enough of the header showing to grab it again. */
const EDGE_MARGIN = 8;
const HEADER_HEIGHT = 34;

/** How much of a window has to stay on screen for it to be grabbable. */
const KEEP_VISIBLE = 48;

/**
 * Keeps a window inside the viewport, however the screen has changed since.
 *
 * The whole of it, not merely a grabbable strip of its header. A window is
 * moved down until its bottom is on screen; one that is taller than the screen
 * cannot manage that and is pinned to the top instead, where at least the first
 * of it can be read and the rest scrolled to. The header rule is what is left
 * when nothing else can be honoured.
 */
function clamp(position: WindowPosition, size: { width: number; height: number }): WindowPosition {
  const maxX = Math.max(EDGE_MARGIN, window.innerWidth - size.width - EDGE_MARGIN);
  const maxY = Math.max(
    EDGE_MARGIN,
    Math.min(
      window.innerHeight - size.height - EDGE_MARGIN,
      window.innerHeight - HEADER_HEIGHT - EDGE_MARGIN,
    ),
  );
  return {
    x: Math.min(Math.max(position.x, EDGE_MARGIN), maxX),
    y: Math.min(Math.max(position.y, EDGE_MARGIN), maxY),
  };
}

/**
 * Pulls a window onto the nearest edge worth lining up with.
 *
 * The edges of the screen, and the edges of every other window that is open:
 * dragging a panel next to another one should leave them flush rather than two
 * pixels apart. Only windows that actually sit alongside are considered, or a
 * panel in the far corner would tug at one being dragged across the middle.
 */
function snap(
  wanted: WindowPosition,
  size: { width: number; height: number },
  self: HTMLElement,
): WindowPosition {
  const acrossX: number[] = [EDGE_MARGIN, window.innerWidth - size.width - EDGE_MARGIN];
  const acrossY: number[] = [EDGE_MARGIN, window.innerHeight - size.height - EDGE_MARGIN];

  const mine = {
    left: wanted.x,
    right: wanted.x + size.width,
    top: wanted.y,
    bottom: wanted.y + size.height,
  };

  for (const other of document.querySelectorAll<HTMLElement>('[data-window]')) {
    if (other === self) continue;

    const box = other.getBoundingClientRect();
    if (box.width < 16 || box.height < 16) continue;

    // Alongside each other: the spans have to overlap, or lining them up means
    // nothing to anybody looking at the screen.
    const beside = mine.bottom > box.top - SNAP && mine.top < box.bottom + SNAP;
    const above = mine.right > box.left - SNAP && mine.left < box.right + SNAP;

    if (beside) acrossX.push(box.left, box.right, box.left - size.width, box.right - size.width);
    if (above) acrossY.push(box.top, box.bottom, box.top - size.height, box.bottom - size.height);
  }

  const nearest = (held: number, lines: number[]): number => {
    let best = held;
    let gap = SNAP;

    for (const line of lines) {
      const off = Math.abs(line - held);
      if (off < gap) {
        gap = off;
        best = line;
      }
    }

    return best;
  };

  return { x: nearest(wanted.x, acrossX), y: nearest(wanted.y, acrossY) };
}

/**
 * Where a window goes on the screen as it is now.
 *
 * The layout's answer if it has one, then the window's own idea of where it
 * starts, then the middle of the screen, which is where something demanding
 * attention belongs.
 */
function whereFor(
  kind: string,
  size: { width: number; height: number },
  home: ((size: { width: number; height: number }) => WindowPosition) | undefined,
): WindowPosition {
  return (
    positionFor(kind, size) ??
    (home
      ? home(size)
      : { x: (window.innerWidth - size.width) / 2, y: (window.innerHeight - size.height) / 2 })
  );
}

export interface DraggableWindow {
  /** Put on the window itself, so it can be measured and placed. */
  attach: (element: HTMLDivElement | null) => void;
  style: { left: number; top: number };
  /** Put on the window's header, which is what a player grabs. */
  onPointerDown: (event: React.PointerEvent) => void;
  /**
   * Shifts the window by a step, for something else moving it.
   *
   * Resizing by a corner has to move the window when the corner being pulled is
   * a left or a top one, because the corner opposite is the one meant to be
   * standing still. That is the only caller: dragging goes through the header.
   */
  nudge: (dx: number, dy: number) => void;
  /**
   * Whether the last gesture moved the window rather than pressing it.
   *
   * Only interesting for something that is a control and a handle at once, like
   * the run meter: it has to know whether the press that just finished was a
   * click or the end of a drag.
   */
  wasDragged: () => boolean;
}

export interface DraggableOptions {
  /** Where it goes the first time, given the screen and its own size. */
  home?: (size: { width: number; height: number }) => WindowPosition;
  /**
   * Whether a press on a control inside counts as a grip.
   *
   * Off by default: a header full of buttons has to let them be pressed. On for
   * anything that is one control from edge to edge and still has to be movable.
   */
  grabAnywhere?: boolean;
}

/**
 * Places a window and lets it be dragged by its header.
 *
 * The first time it is opened it goes in the middle of the screen, which is where
 * something demanding attention belongs. After that it goes back where it was put.
 */
export function useDraggableWindow(kind: string, options: DraggableOptions = {}): DraggableWindow {
  const { home, grabAnywhere = false } = options;
  const [position, setPosition] = useState<WindowPosition | null>(null);
  const element = useRef<HTMLDivElement | null>(null);
  const grab = useRef<{ pointer: number; dx: number; dy: number } | null>(null);

  /** Whether the gesture that just finished moved the window. */
  const dragged = useRef(false);

  /**
   * The latest place the window was dragged to.
   *
   * Kept alongside the state because what is written down at the end of a drag has
   * to be where the window was last put, and the element itself is a frame behind:
   * React has not applied the last move by the time the finger comes up.
   */
  const latest = useRef<WindowPosition | null>(null);

  /**
   * Where it goes the first time, kept where the ref callback can reach it.
   *
   * A caller writing this inline hands over a new function on every render, and a
   * ref callback that changes identity is called again by React: the window was
   * put back where it was stored, one frame, every time anything inside it
   * changed. Which is exactly what a window flickering while being dragged is.
   */
  const first = useRef(home);

  useEffect(() => {
    first.current = home;
  }, [home]);

  const place = useCallback(
    (node: HTMLDivElement | null) => {
      element.current = node;
      if (!node) return;

      // Named in the document, so one window being dragged can find the others
      // to line up with without anything having to keep a list of them.
      node.dataset.window = kind;

      const size = { width: node.offsetWidth, height: node.offsetHeight };

      // Already placed: this is the same window being handed over again, not a
      // new one opening, so where it is now is where it stays.
      if (latest.current) {
        setPosition(clamp(latest.current, size));
        return;
      }

      const placed = clamp(whereFor(kind, size, first.current), size);
      latest.current = placed;
      setPosition(placed);
    },
    [kind],
  );

  /*
   * A different size of screen, or a different layout, is asked about afresh.
   *
   * Not shoved inside the new edges from wherever it happens to be: that is
   * what left windows piled on each other after a resize, and still piled on
   * each other after going back, because the shoved place was the only one
   * anything remembered. The layout knows where this goes on this screen.
   */
  useEffect(() => {
    const again = (): void => {
      const node = element.current;
      if (!node || grab.current) return;

      const size = { width: node.offsetWidth, height: node.offsetHeight };
      const placed = clamp(whereFor(kind, size, first.current), size);
      latest.current = placed;
      setPosition(placed);
    };

    window.addEventListener('resize', again);
    window.addEventListener(LAYOUT_EVENT, again);
    return () => {
      window.removeEventListener('resize', again);
      window.removeEventListener(LAYOUT_EVENT, again);
    };
  }, [kind]);

  // A window that grows should not grow off the screen. Settings opened on its
  // shortest page and was placed to fit; switching to the keys, which is twice
  // as long, pushed its last row off the bottom, because nothing had asked
  // again since it was put there. So its own size is watched.
  useEffect(() => {
    const node = element.current;

    const again = (): void => {
      const held = element.current;
      if (!held || !position) return;

      const next = clamp(position, { width: held.offsetWidth, height: held.offsetHeight });
      if (next.x === position.x && next.y === position.y) return;

      latest.current = next;
      setPosition(next);
    };

    const watching = node ? new ResizeObserver(again) : null;
    if (node && watching) watching.observe(node);

    return () => watching?.disconnect();
  }, [position]);

  const nudge = useCallback(
    (dx: number, dy: number) => {
      const node = element.current;
      const held = latest.current;
      if (!node || !held) return;

      // A looser bound than dragging uses, on purpose. A window resized by a
      // corner has to move so that the far corner stands still, and one already
      // sitting against an edge cannot do that if it is pulled back to a tidy
      // margin at every step: the corner it is meant to be holding still crawls.
      // Enough of it stays on screen to be grabbed again, which is the rule that
      // actually matters.
      const size = { width: node.offsetWidth, height: node.offsetHeight };
      const moved = {
        x: Math.min(
          Math.max(held.x + dx, KEEP_VISIBLE - size.width),
          window.innerWidth - KEEP_VISIBLE,
        ),
        y: Math.min(Math.max(held.y + dy, 0), window.innerHeight - KEEP_VISIBLE),
      };

      latest.current = moved;
      setPosition(moved);
      keepPosition(kind, moved, size);
    },
    [kind],
  );

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      const node = element.current;
      if (!node || event.button !== 0) return;

      // A control in the header is a control, not a handle. Dragging captures the
      // pointer and swallows the default, so without this the close button never
      // sees the click that follows and the window cannot be shut. Something that
      // is a control from edge to edge says so and sorts the two out itself.
      if (!grabAnywhere && event.target instanceof Element && event.target.closest('button')) {
        return;
      }

      dragged.current = false;

      const box = node.getBoundingClientRect();
      const start = { x: event.clientX, y: event.clientY };
      grab.current = {
        pointer: event.pointerId,
        dx: event.clientX - box.left,
        dy: event.clientY - box.top,
      };

      // A window that is entirely a control keeps the press, so the click that
      // follows still reaches it; everything else swallows it, so a drag over the
      // world does not also do whatever was under the pointer.
      if (!grabAnywhere) {
        (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
        event.preventDefault();
      }

      const move = (moved: PointerEvent): void => {
        const held = grab.current;
        if (!held || moved.pointerId !== held.pointer) return;

        // A few pixels of travel is a press with a shaky hand, not a drag.
        if (Math.abs(moved.clientX - start.x) + Math.abs(moved.clientY - start.y) > DRAG_SLOP) {
          dragged.current = true;
        }

        const size = { width: node.offsetWidth, height: node.offsetHeight };
        const wanted = { x: moved.clientX - held.dx, y: moved.clientY - held.dy };

        // Shift puts it exactly where the pointer is, for the one time in ten
        // that somebody wants a window two pixels off an edge. Alt is what
        // reveals the grips and the ghosts, and is spoken for.
        const next = clamp(moved.shiftKey ? wanted : snap(wanted, size, node), size);

        latest.current = next;
        setPosition(next);
      };

      const release = (released: PointerEvent): void => {
        const held = grab.current;
        if (!held || released.pointerId !== held.pointer) return;

        grab.current = null;
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', release);
        window.removeEventListener('pointercancel', release);

        if (latest.current) {
          keepPosition(kind, latest.current, {
            width: node.offsetWidth,
            height: node.offsetHeight,
          });
        }
      };

      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', release);
      window.addEventListener('pointercancel', release);
    },
    [kind, grabAnywhere],
  );

  return {
    attach: place,
    // Off screen until measured, so it is never seen in the corner first.
    style: position ? { left: position.x, top: position.y } : { left: -9999, top: -9999 },
    onPointerDown,
    nudge,
    wasDragged: () => dragged.current,
  };
}
